import { ResultOf } from 'gql.tada';
import type { DynamicContentWebLink, DynamicContentsItem } from '../types';
import { DynamicContentName } from '../types';
import { buttonFragment } from '../../../../graphql/fragments/buttonFragment';
import { textFragment } from '../../../../graphql/fragments/textFragment';
import { weblinkFragment } from '../../../../graphql/fragments/weblinkFragment';
import { mapApiWeblinkIconInfoToLinkIconInfo } from '../../../helpers/mapApiWeblinkIconInfoToLinkIconInfo';
import { mapApiWeblinkVariantToLinkVariant } from '../../../helpers/mapApiWeblinkVariantToLinkVariant';
import { isNonEmptyArray } from '../../../helpers/arrayHelpers';
import { isNotNullOrUndefinedValue } from '../../../../helpers/isNotNullOrUndefinedValue';
import { mapButtonVariant } from '../../../helpers/mapButtonVariant';

export type ButtonFragment = ResultOf<typeof buttonFragment>;
export type WebLinkFragment = ResultOf<typeof weblinkFragment>;
export type TextFragment = ResultOf<typeof textFragment>;

type WebLinkFromAPIWithoutTypename = Omit<
  WebLinkFragment,
  '__typename' | 'iconInfo' | 'tracking'
> & {
  iconInfo?: Omit<NonNullable<WebLinkFragment['iconInfo']>, '__typename'> | null;
  tracking?:
    | (Omit<
        NonNullable<WebLinkFragment['tracking']>,
        '__typename' | 'outboundTracking' | 'clickThroughTracking'
      > & {
        outboundTracking?: Omit<
          Omit<
            NonNullable<NonNullable<WebLinkFragment['tracking']>['outboundTracking']>,
            'data'
          > & {
            data: Omit<
              NonNullable<
                NonNullable<NonNullable<WebLinkFragment['tracking']>['outboundTracking']>['data']
              >,
              '__typename'
            >;
          },
          '__typename'
        > | null;
        clickThroughTracking?: Omit<
          NonNullable<NonNullable<WebLinkFragment['tracking']>['clickThroughTracking']>,
          '__typename'
        > | null;
      })
    | null;
};

export interface ButtonFromAPIWithClickHandler extends ButtonFragment {
  onClick?: () => void;
}

export const mapWebLink = (webLink: WebLinkFromAPIWithoutTypename): DynamicContentWebLink => {
  return {
    __typename: 'WebLink',
    title: webLink.title,
    url: webLink.url,
    isExternal: webLink.isExternal,
    variant: mapApiWeblinkVariantToLinkVariant(webLink.variant),
    iconInfo: webLink.iconInfo ? mapApiWeblinkIconInfoToLinkIconInfo(webLink.iconInfo) : null,
    name: DynamicContentName.WebLink,
    ...(webLink.rel && { rel: webLink.rel }),
    tracking: {
      outboundTracking: webLink.tracking?.outboundTracking
        ? {
            schema: webLink.tracking.outboundTracking.schema,
            data: {
              event_name: webLink.tracking.outboundTracking.data.event_name,
              type: webLink.tracking.outboundTracking.data.type,
              click_url: webLink.tracking.outboundTracking.data.click_url,
            },
          }
        : null,
      clickThroughTracking: webLink.tracking?.clickThroughTracking
        ? {
            pathname: webLink.tracking.clickThroughTracking.pathname,
            page: webLink.tracking.clickThroughTracking.page,
            element: webLink.tracking.clickThroughTracking.element,
            sourceType: webLink.tracking.clickThroughTracking.sourceType,
          }
        : null,
    },
    ...(typeof webLink.disabled === 'boolean' && { disabled: webLink.disabled }),
  };
};

export const mapDynamicContentItem = (
  dynamicContentItem:
    | ButtonFragment
    | ButtonFromAPIWithClickHandler
    | TextFragment
    | WebLinkFragment
    | null
    | undefined,
): DynamicContentsItem | null => {
  if (!dynamicContentItem) return null;

  switch (dynamicContentItem.__typename) {
    case 'Button':
      return {
        ...dynamicContentItem,
        __typename: dynamicContentItem.__typename,
        name: DynamicContentName.Button,
        buttonVariant: mapButtonVariant(dynamicContentItem.buttonVariant),
      };
    case 'WebLink':
      return mapWebLink(dynamicContentItem);
    case 'Text':
      return {
        ...dynamicContentItem,
        __typename: dynamicContentItem.__typename,
        name: DynamicContentName.Text,
      };
    default:
      return null;
  }
};

export const mapDynamicContentItems = (
  dynamicContentItems?:
    | (ButtonFragment | TextFragment | WebLinkFragment | null | undefined)[]
    | null,
): DynamicContentsItem[] => {
  if (!isNonEmptyArray(dynamicContentItems)) return [];
  return dynamicContentItems
    .map(mapDynamicContentItem)
    .filter(Boolean)
    .filter(isNotNullOrUndefinedValue);
};
